import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  Checkbox,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { getAuthToken } from "../../utils/getAuthToken";

// Calling has its own dedicated single-instance service — falls back to the
// main API server only if REACT_APP_CALLING_URL is unset (e.g. not yet configured).
const SERVER_DOMAIN = process.env.REACT_APP_CALLING_URL || process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

const DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const DAY_LABELS = { MONDAY: "Monday", TUESDAY: "Tuesday", WEDNESDAY: "Wednesday", THURSDAY: "Thursday", FRIDAY: "Friday", SATURDAY: "Saturday", SUNDAY: "Sunday" };
// Wraps SUNDAY -> MONDAY — needed to carry an overnight range (e.g. Sunday 23:00 -> Monday 04:00) forward.
const NEXT_DAY = { MONDAY: "TUESDAY", TUESDAY: "WEDNESDAY", WEDNESDAY: "THURSDAY", THURSDAY: "FRIDAY", FRIDAY: "SATURDAY", SATURDAY: "SUNDAY", SUNDAY: "MONDAY" };

// What <input type="time"> actually produces — "HH:MM", 24h, zero-padded. Sanitization
// guard for the rare case a value reaches handleSave malformed (e.g. a partially-typed
// value on browsers that allow free text entry into the native time widget).
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// A practical subset, not every IANA zone — covers the common cases without
// turning this into an unmanageable 400-item dropdown.
const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Sao_Paulo", "America/Mexico_City", "America/Manaus",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid", "Europe/Moscow",
  "Africa/Cairo", "Africa/Lagos", "Africa/Johannesburg",
  "Asia/Dubai", "Asia/Karachi", "Asia/Kolkata", "Asia/Dhaka", "Asia/Bangkok",
  "Asia/Singapore", "Asia/Shanghai", "Asia/Tokyo", "Asia/Seoul",
  "Australia/Sydney", "Pacific/Auckland",
];

// Meta wants "HHMM" (e.g. "0900"); the <input type="time"> gives "09:00".
const toMeta = (hhmm) => (hhmm ? hhmm.replace(":", "") : "");
const fromMeta = (hhmm) => (hhmm && hhmm.length === 4 ? `${hhmm.slice(0, 2)}:${hhmm.slice(2)}` : "");

async function authGet(path) {
  const token = await getAuthToken();
  const res = await axios.get(`${SERVER_DOMAIN}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  return res.data;
}
async function authPost(path, body) {
  const token = await getAuthToken();
  const res = await axios.post(`${SERVER_DOMAIN}${path}`, body, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return res.data;
}

function buildInitialDayState() {
  const state = {};
  DAYS.forEach((day) => {
    state[day] = { enabled: false, open: "09:00", close: "17:00" };
  });
  return state;
}

/**
 * Meta's weekly_operating_hours has no native "spans midnight" concept — a single
 * entry must have close_time strictly after open_time on the same day (see
 * buildWeeklyOperatingHours below for the save-side split that works around this).
 * This is the inverse: given the flat list Meta returns, fold a split-overnight pair
 * (day D closing at 23:59 immediately followed by day D+1 opening at 00:00) back into
 * one UI row on day D — e.g. Monday 23:00-23:59 + Tuesday 00:00-04:00 reappears as a
 * single "Monday 23:00 to 04:00" row, instead of a misleading standalone Tuesday block.
 */
function reconstructDaysFromMeta(entries) {
  const byDay = {};
  entries.forEach((e) => {
    if (!byDay[e.day_of_week]) byDay[e.day_of_week] = [];
    byDay[e.day_of_week].push(e);
  });

  const next = buildInitialDayState();
  const consumedCarryoverKeys = new Set();

  DAYS.forEach((day) => {
    // open_time !== "0000" excludes a plain full-day 00:00-23:59 entry, which isn't
    // an overnight carry-in — it's just a normal same-day entry already.
    const overnightEntry = (byDay[day] || []).find((e) => e.close_time === "2359" && e.open_time !== "0000");
    if (!overnightEntry) return;
    const nextDay = NEXT_DAY[day];
    const carry = (byDay[nextDay] || []).find((e) => e.open_time === "0000");
    if (!carry) return;
    next[day] = { enabled: true, open: fromMeta(overnightEntry.open_time) || "09:00", close: fromMeta(carry.close_time) || "17:00" };
    consumedCarryoverKeys.add(`${nextDay}|${carry.open_time}|${carry.close_time}`);
  });

  DAYS.forEach((day) => {
    if (next[day].enabled) return; // already resolved as an overnight row above
    const entry = (byDay[day] || []).find((e) => !consumedCarryoverKeys.has(`${day}|${e.open_time}|${e.close_time}`));
    if (entry) {
      next[day] = { enabled: true, open: fromMeta(entry.open_time) || "09:00", close: fromMeta(entry.close_time) || "17:00" };
    }
  });

  return next;
}

/**
 * Validates each enabled day's open/close pair and splits any overnight range (close
 * <= open, e.g. 11:00 -> 04:00) into two same-day Meta entries — today's remainder up
 * to 23:59, and tomorrow's 00:00 up to the close time. Without this, Meta's Graph API
 * rejects a single 11:00-04:00 entry outright with a bare "Invalid parameter" error,
 * since it has no way to know that's meant to cross midnight rather than being backwards.
 * Returns { error } on the first invalid day, or { weekly_operating_hours } on success.
 */
function buildWeeklyOperatingHours(days, enabledDays) {
  const weekly_operating_hours = [];

  for (const d of enabledDays) {
    const { open, close } = days[d];
    if (!TIME_RE.test(open) || !TIME_RE.test(close)) {
      return { error: `${DAY_LABELS[d]}: enter both an open and a close time` };
    }
    if (open === close) {
      return { error: `${DAY_LABELS[d]}: open and close time can't be the same` };
    }

    if (close > open) {
      // Normal same-day range.
      weekly_operating_hours.push({ day_of_week: d, open_time: toMeta(open), close_time: toMeta(close) });
    } else if (close === "00:00") {
      // Ends exactly at midnight — 23:59 is near enough and avoids pushing a
      // zero-length "00:00-00:00" entry onto the next day.
      weekly_operating_hours.push({ day_of_week: d, open_time: toMeta(open), close_time: "2359" });
    } else {
      // Overnight, e.g. 11:00 -> 04:00: split across the day boundary.
      weekly_operating_hours.push({ day_of_week: d, open_time: toMeta(open), close_time: "2359" });
      weekly_operating_hours.push({ day_of_week: NEXT_DAY[d], open_time: "0000", close_time: toMeta(close) });
    }
  }

  return { weekly_operating_hours };
}

export default function CallsOfficeHours() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [timezone, setTimezone] = useState("America/New_York");
  const [days, setDays] = useState(buildInitialDayState);

  useEffect(() => {
    (async () => {
      try {
        const { callHours } = await authGet("/api/calling/business-hours");
        setEnabled(callHours.status === "ENABLED");
        if (callHours.timezone_id) setTimezone(callHours.timezone_id);
        if (callHours.weekly_operating_hours?.length) {
          setDays(reconstructDaysFromMeta(callHours.weekly_operating_hours));
        }
      } catch (err) {
        toast.error("Could not load calling business hours");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDayToggle = (day) => setDays((prev) => ({ ...prev, [day]: { ...prev[day], enabled: !prev[day].enabled } }));
  const handleTimeChange = (day, field, value) => setDays((prev) => ({ ...prev, [day]: { ...prev[day], [field]: value } }));

  const handleSave = async () => {
    // Only the enabled days are actually required to have valid times — a disabled
    // day's fields are inert, so they're never even looked at here.
    const enabledDays = DAYS.filter((d) => days[d].enabled);

    if (enabled && enabledDays.length === 0) {
      toast.error("Turn on at least one day, or disable business hours entirely");
      return;
    }

    const { weekly_operating_hours, error } = buildWeeklyOperatingHours(days, enabledDays);
    if (error) {
      toast.error(error);
      return;
    }

    setSaving(true);
    try {
      await authPost("/api/calling/business-hours", {
        status: enabled ? "ENABLED" : "DISABLED",
        timezone_id: timezone,
        weekly_operating_hours,
      });
      toast.success("Business hours saved — WhatsApp clients can take up to 7 days to reflect this, though the server honors it immediately");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save business hours");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Office Hours
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Restrict user-initiated calls to these hours. Outside them, WhatsApp offers callers the option to message
        instead, or request a callback if that's enabled.
      </Typography>

      <FormControlLabel
        control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
        label={enabled ? "Business hours enabled" : "Calling available 24/7 (business hours disabled)"}
        sx={{ mb: 2 }}
      />

      {enabled && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Timezone
          </Typography>
          <Select size="small" value={timezone} onChange={(e) => setTimezone(e.target.value)} sx={{ minWidth: 260, mb: 3 }}>
            {TIMEZONES.map((tz) => (
              <MenuItem key={tz} value={tz}>
                {tz}
              </MenuItem>
            ))}
          </Select>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Weekly hours
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
            To run overnight into the next day (e.g. 11:00 AM to 4:00 AM), just set a close time earlier than the
            open time — it's handled automatically.
          </Typography>
          {DAYS.map((day) => {
            const { enabled: dayEnabled, open, close } = days[day];
            const isOvernight = dayEnabled && TIME_RE.test(open) && TIME_RE.test(close) && close <= open;
            return (
            <Box key={day} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.5 }}>
              <Checkbox checked={days[day].enabled} onChange={() => handleDayToggle(day)} size="small" />
              <Typography sx={{ width: 100, fontSize: 14 }}>{DAY_LABELS[day]}</Typography>
              <TextField
                type="time"
                size="small"
                required={days[day].enabled}
                value={days[day].open}
                disabled={!days[day].enabled}
                onChange={(e) => handleTimeChange(day, "open", e.target.value)}
                sx={{ width: 130 }}
              />
              <Typography sx={{ color: "text.secondary" }}>to</Typography>
              <TextField
                type="time"
                size="small"
                required={days[day].enabled}
                value={days[day].close}
                disabled={!days[day].enabled}
                onChange={(e) => handleTimeChange(day, "close", e.target.value)}
                sx={{ width: 130 }}
              />
              {isOvernight && (
                <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  → next day
                </Typography>
              )}
            </Box>
            );
          })}
        </>
      )}

      <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ mt: 3 }}>
        {saving ? "Saving…" : "Save"}
      </Button>
    </Box>
  );
}
